import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  LinkedDataPosition,
  PaginatedFetch,
  PaginatedMeta,
  ExportResponse,
  ExportOptions,
} from '@types'

import { PAGE_SIZE } from '@/const/common'

import { prepareResponseError, updateStoreListItem } from '../utils/helpers'
import { prepareExportParams } from '@/helpers/common'

import { useRepositoriesStore } from '../repositories/index'

import api, { getPaginatedRequest } from '@/store/api'

export const useLinkedDataPositionsStore = defineStore(
  'linked-data-positions',
  () => {
    // INIT
    const repositoriesStore = useRepositoriesStore()

    const list = ref<LinkedDataPosition[]>([])

    const initFlag = ref(false)

    const loading = ref(true)

    const meta = ref<PaginatedMeta>()

    let abortController = new AbortController()

    const repositoryId = computed(() => repositoriesStore.getCurrentId)

    // GETTERS
    const getList = computed(() => list.value)

    const getMeta = computed(() => meta.value)

    // SETTERS
    const setList = (
      data: LinkedDataPosition[],
      metaData?: PaginatedMeta,
    ) => {
      list.value = data
      meta.value = metaData
    }

    const setPosition = (position: LinkedDataPosition) => {
      updateStoreListItem(list.value, position)
    }

    // ACTIONS

    const fetch: PaginatedFetch = async (options, store = true, signal) => {
      if (store) loading.value = true
      try {
        const { data, meta } = await getPaginatedRequest<LinkedDataPosition>(
          'linked_data/positions',
          {
            repository_id: repositoryId.value,
            page_size: PAGE_SIZE,
            ...options,
          },
          signal,
        )
        if (store) {
          setList(data, meta)
          initFlag.value = true
        } else {
          return { data, meta }
        }
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        if (store) loading.value = false
      }
    }

    const fetchSingle = async (positionId: string, signal?: AbortSignal) => {
      try {
        const response = await api.get<any>(
          `linked_data/positions/${positionId}`,
          {
            params: { repository_id: repositoryId.value },
            signal,
          },
        )
        return response.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const exportList = async (options: ExportOptions) => {
      try {
        const params = prepareExportParams(options)
        const response = await api.get<ExportResponse>(
          'linked_data/positions/export',
          {
            params: { repository_id: repositoryId.value, ...params }
          },
        )
        const url = response.data?.file_url
        if (!url) throw Error('Linked Data Positions export URL is not available')
        const json = await api.get(url)
        return json.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const cancel = () => {
      abortController.abort()
      abortController = new AbortController()
    }

    const clear = () => {
      initFlag.value = false

      loading.value = true

      setList([], undefined)
    }

    return {
      initFlag,
      loading,

      getList,
      getMeta,
      setPosition,

      fetch,
      fetchSingle,

      export: exportList,

      cancel,
      clear,
    }
  },
)
